import type { Requirement } from '@ben/columbia/api/requirements';
import GalleryDrawer from '@ben/orion/gallery-drawer';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { Code, Stack, Text } from '@mantine/core';
import styled from 'styled-components';
import { domainLabel } from './domains';
import { Metadata } from './shared';
import { StatusTag } from './status';
import type { WorkspaceStatus } from './status';
import { useGroupDetail } from './use-group-detail';

export const RequirementDrawer = () => {
  const {
    openRequirement,
    openIndex,
    totalVisible,
    onPrevious,
    onNext,
    onCloseRequirement,
  } = useGroupDetail();

  return (
    <GalleryDrawer
      title={openRequirement?.name ?? ''}
      isOpen={openRequirement !== undefined}
      onPrevious={openIndex > 0 ? onPrevious : undefined}
      onNext={openIndex < totalVisible - 1 ? onNext : undefined}
      onClose={onCloseRequirement}
      labels={{
        leftArrowLabel: 'Previous requirement',
        rightArrowLabel: 'Next requirement',
      }}
    >
      {openRequirement ? (
        <DrawerBody>
          <RequirementContent requirement={openRequirement} />
        </DrawerBody>
      ) : null}
    </GalleryDrawer>
  );
};

const DrawerBody = styled.div`
  padding-left: ${({ theme }) => theme.spacing(3)};
`;

const RequirementContent = ({ requirement }: { requirement: Requirement }) => (
  <Stack gap="md">
    <div>
      <Text size="sm" c="dimmed">
        {domainLabel(requirement.group.domain)}
      </Text>
      <Typography variant={TypographyVariant.bodyLg2}>
        {requirement.name}
      </Typography>
    </div>

    <div>
      <Typography variant={TypographyVariant.labelSm}>Answer</Typography>
      <Typography variant={TypographyVariant.bodyMd1}>
        {requirement.body || 'Not answered yet.'}
      </Typography>
    </div>

    <Metadata>
      <dt>Status</dt>
      <dd>
        <StatusTag status={requirement.status as WorkspaceStatus} />
      </dd>
      <dt>Confidence</dt>
      <dd>{requirement.confidence}</dd>
      <dt>Captured</dt>
      <dd>{new Date(requirement.createdAt).toLocaleDateString()}</dd>
      <dt>Versions</dt>
      <dd>
        {requirement.supersedes.length > 0
          ? `Replaced ${requirement.supersedes.length} earlier version(s)`
          : 'First version'}
      </dd>
    </Metadata>

    {requirement.intent ? (
      <div>
        <Typography variant={TypographyVariant.labelSm}>
          Why it was captured
        </Typography>
        <Text size="sm" c="dimmed">
          {requirement.intent}
        </Text>
      </div>
    ) : null}

    <div>
      <Typography variant={TypographyVariant.labelSm}>Evidence</Typography>
      <Code block mah={420}>
        {JSON.stringify(requirement.source, null, 2)}
      </Code>
    </div>
  </Stack>
);
